import {useContext, useEffect, useState} from 'react';
import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Button,
    ModalBody,
    Form,
    Label,
    Input,
    Modal, ModalHeader, FormGroup, FormFeedback
} from "reactstrap";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {toast} from "react-toastify";
import Loader from "../../components/Loader";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import AuthContext from "../../context/AuthContext";
import {
    changeProductGroupStatus,
    editProductGroup,
    getProducts,
} from "../../api/api";
import Select from "react-select";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";


function ActionButton(props) {
    const {refreshToken} = useContext(AuthContext)
    const [actionButtonDropdownOpen, setActionButtonDropdownOpen] = useState(false);
    const toggleActionButtonDropdown = () => {
        setActionButtonDropdownOpen(prevState => {
            const newDropdownState = !prevState;
            document.querySelector('.table-responsive').classList.toggle('dropdown-open', newDropdownState);
            return newDropdownState;
        });
    }
    const [productGroupUpdateToggle, setProductGroupUpdateToggle] = useState(false);

    const toggleProductGroupUpdateModal = () => {
        setProductGroupUpdateToggle(prevState => {
            const newState = !prevState;
            if (!newState) resetFormFields();
            return newState;
        });
    };

    const [productGroupName, setProductGroupName] = useState(props.productGroup.name);
    const [productGroupPrice, setProductGroupPrice] = useState(props.productGroup.price);
    const [productGroupImage, setProductGroupImage] = useState('');
    const [productGroupDescription, setProductGroupDescription] = useState(props.productGroup.description);
    const [productsSupported, setProductsSupported] = useState(
        props.productGroup.products.map(product => product.name)
    );
    const [currentProductsSupported, setCurrentProductsSupported] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [productGroupRecords, setProductGroupRecords] = useState(1);
    const [productGroupHSNSAC, setProductGroupHSNSAC] = useState(props.productGroup.hsn_sac);
    const [productGroupUnitOfMeasure, setProductGroupUnitOfMeasure] = useState(props.productGroup.unit_of_measure);
    const [productGroupTaxType,setProductGroupTaxType] = useState('');
    const [productGroupTaxPercentage, setProductGroupTaxPercentage] = useState('');
    const [advancedOptions, setAdvancedOptions] = useState(false);

    const queryClient = useQueryClient()

    const resetFormFields = () => {
        setProductGroupName(props.productGroup.name);
    };

    useEffect(() => {
        const validProducts = productsSupported
            .filter(product => product && typeof product === 'string')
            .map((product) => ({
                value: product,
                label: product,
            }));
        setCurrentProductsSupported(validProducts);
    }, [productsSupported]);

    useEffect(() => {
        const currentDefault = props.productGroup.products
            .filter(product => product.name && typeof product.name === 'string')
            .map(product => ({
                value: product.name,
                label: product.name,
            }));
        setCurrentProductsSupported(currentDefault);
    }, [props.productGroup.products]);

    useEffect(() => {
        if (props.productGroup.products) {
            const initialProductsSupported = props.productGroup.products
                .filter(product => product.name && typeof product.name === 'string')
                .map((product) => ({
                    value: product.name,
                    label: product.name,
                }));
            setProductsSupported(initialProductsSupported.map(p => p.value));
            setCurrentProductsSupported(initialProductsSupported);
        }
    }, [props.productGroup.products]);

    const onSelect = (selectedOptions) => {
        const selectedProducts = selectedOptions
            .map((option) => option.value)
            .filter((value) => value);
        setProductsSupported(selectedProducts);
    };

    const changeProductGroupStatusMutation = useMutation({
        mutationFn: changeProductGroupStatus,
        onSuccess: (data, variables) =>{
            queryClient.invalidateQueries(['product-groups'])
            toast.success('Product Group Status Changed!')
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, changeProductGroupStatusMutation, variables)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
        }
    })

    const { refetch } = useQuery({
        queryKey: ['products', productGroupRecords],
        queryFn: () => getProducts(1,'', '','','', '', '','',
            '', '','', '','', '','', productGroupRecords),
        keepPreviousData : true,
        retry: false,
        refetchOnWindowFocus: false,
        enabled: productGroupUpdateToggle,
        onSuccess: (data => {
            if (data?.results?.length > 0) {
                setProductGroupRecords(data.count);

                const newOptions = data.results
                    .filter((product) => product.name)
                    .map((product) => ({
                        value: product.name,
                        label: product.name,
                    }));
                setProductOptions(newOptions);
            }

            if (props.productGroup.products) {
                const initialProductsSupported = props.productGroup.products
                    .filter((product) => product.name)
                    .map((product) => product.name);
                setProductsSupported(initialProductsSupported);
            }
        }),
        onError: (err => {
            handleUnauthorized401QueryError(err, refreshToken, refetch)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
        })
    })


    const editProductGroupMutation = useMutation({
        mutationFn: editProductGroup,
        onSuccess: (data, variables) =>{
            toggleProductGroupUpdateModal()
            queryClient.invalidateQueries(['product-groups'])
            setProductsSupported(data.products)
            toast.success('Product Group updated!')
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, editProductGroupMutation, variables)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
        }
    })

    const handleChangeProductGroupStatus = async (productGroup) => {
        let productGroupStatus = {
            status: productGroup.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
        };
        changeProductGroupStatusMutation.mutate({
            productGroupStatus: productGroupStatus, productGroupId: productGroup.id
        })
    }

    const handleEditProductGroup = async (productGroup, e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("name", productGroupName);
        formData.append("price", productGroupPrice);
        formData.append("description", productGroupDescription);
        formData.append("image", productGroupImage);
        formData.append("hsn_sac", productGroupHSNSAC);
        formData.append("unit_of_measure", productGroupUnitOfMeasure);
        formData.append("tax_type", productGroupTaxType);
        formData.append("tax_percentage", productGroupTaxPercentage);

        productsSupported.forEach(product => {formData.append("products", product)})

        editProductGroupMutation.mutate({
            formData: formData, productGroupId: productGroup.id
        }, )
    };

    return (
        <td className="text-center">
            <Dropdown
                nav
                isOpen={actionButtonDropdownOpen}
                toggle={toggleActionButtonDropdown}
                className="btn-group dropdown"
            >
                <DropdownToggle
                    caret
                    className="dropdown-toggle arrow-none btn btn-light btn-sm"
                >
                    <i className="mdi mdi-dots-horizontal"/>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-right profile-dropdown">
                    <DropdownItem
                        className="dropdown-item"
                        onClick={toggleProductGroupUpdateModal}
                    >
                        <i className="mdi mdi-pencil mr-2 text-muted font-18 vertical-middle"/>
                        <span>Edit Group</span>
                    </DropdownItem>

                    <DropdownItem
                        className="dropdown-item"
                        onClick={() => handleChangeProductGroupStatus(props.productGroup)}
                    >
                        <i className="mdi mdi-delete mr-2 text-muted font-18 vertical-middle"/>
                        <span>
                                {props.productGroup.status === "ACTIVE"
                                    ? "Make INACTIVE"
                                    : "Make ACTIVE"}
                            </span>
                    </DropdownItem>

                </DropdownMenu>
            </Dropdown>

            <Modal
                isOpen={productGroupUpdateToggle}
                toggle={toggleProductGroupUpdateModal}
                backdrop="static"
                keyboard={false}
                className=""
            >
                <ModalHeader toggle={toggleProductGroupUpdateModal} close={
                    <button className="close" onClick={toggleProductGroupUpdateModal}>
                        &times;
                    </button>
                }>
                    Update Product Group
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={(e) => handleEditProductGroup(props.productGroup, e)}>
                        <FormGroup>
                            <Label for="product-group-name">Name<span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                name="product-group-name"
                                id="product-group-name"
                                placeholder="Product Group name"
                                defaultValue={props.productGroup.name}
                                onChange={(e) =>
                                    setProductGroupName(e.target.value)
                                }
                                invalid={
                                    editProductGroupMutation.isError &&
                                    editProductGroupMutation.error.response.data.name
                                }
                            />
                            {editProductGroupMutation.isError && editProductGroupMutation.error.response.data.name && (
                                <FormFeedback invalid>
                                    {editProductGroupMutation.error.response.data.name}
                                </FormFeedback>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label for="product-group-supported">
                                Select The Products That Supports This Product Groups
                                <span className="text-danger">*</span>
                            </Label>
                            <Select
                                key={props.productGroup.id}
                                options={productOptions}
                                isMulti
                                noOptionsMessage = {() => "No Product(s) found!"}
                                placeholder = "Select Products..."
                                onChange={onSelect}
                                value={currentProductsSupported}
                                required={true}
                                styles={{
                                    control: (baseStyles) => (
                                        {
                                            ...baseStyles,
                                            borderColor: "#495561",
                                            backgroundColor: "#414d59",
                                            color: "white",
                                            padding: "0.45 0.9",
                                            border: 'none',
                                            boxShadow: 'none'
                                        }
                                    ),
                                    menu: (baseStyles) => (
                                        {
                                            ...baseStyles,
                                            backgroundColor: "#414d59",
                                            color: "#a8b5c3"
                                        }
                                    ),
                                    input: (baseStyles) => (
                                        {
                                            ...baseStyles,
                                            color: "white"
                                        }
                                    )
                                }}

                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="product-group-price">Price<span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                min="1"
                                step="any"
                                name="product-group-price"
                                id="product-group-price"
                                placeholder="Product Group Price"
                                defaultValue={props.productGroup.price}
                                onChange={(e) =>
                                    setProductGroupPrice(e.target.value)
                                }
                                invalid={
                                    editProductGroupMutation.isError &&
                                    editProductGroupMutation.error.response.data.price
                                }
                            />
                            {editProductGroupMutation.isError && editProductGroupMutation.error.response.data.price && (
                                <FormFeedback invalid>
                                    {editProductGroupMutation.error.response.data.price}
                                </FormFeedback>
                            )}
                        </FormGroup>
                        <FormGroup className="form-group mb-3">
                            <div className="custom-control custom-checkbox">
                                <Input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id="checkbox-advanced-options"
                                    checked={advancedOptions}
                                    onChange={() => setAdvancedOptions(!advancedOptions)}
                                />
                                <Label
                                    className="custom-control-label"
                                    for="checkbox-advanced-options"                                    >
                                    Advanced Options
                                </Label>
                            </div>
                        </FormGroup>
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="product-group-description">Description</Label>
                                <Input
                                    type="textarea"
                                    name="product-group-description"
                                    id="product-group-description"
                                    defaultValue={props.productGroup.description}
                                    onChange={(e) =>
                                        setProductGroupDescription(e.target.value)
                                    }
                                    invalid={
                                        editProductGroupMutation.isError &&
                                        editProductGroupMutation.error.response.data.description
                                    }
                                />
                                {editProductGroupMutation.isError && editProductGroupMutation.error.response.data.description && (
                                    <FormFeedback invalid>
                                        {editProductGroupMutation.error.response.data.description}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="hsn_sac">HSN/SAC</Label>
                                <Input
                                    type="text"
                                    name="hsn_sac"
                                    id="hsn_sac"
                                    placeholder="HSN/SAC"
                                    defaultValue={props.productGroup.hsn_sac}
                                    onChange={(e) =>
                                        setProductGroupHSNSAC(e.target.value)
                                    }
                                    invalid={
                                        editProductGroupMutation.isError &&
                                        editProductGroupMutation.error.response.data.hsn_sac
                                    }
                                />
                                {editProductGroupMutation.isError && editProductGroupMutation.error.response.data.hsn_sac && (
                                    <FormFeedback invalid>
                                        {editProductGroupMutation.error.response.data.hsn_sac}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="unit_of_measure">Unit of Measure</Label>
                                <Input
                                    type="text"
                                    name="unit_of_measure"
                                    id="unit_of_measure"
                                    placeholder="Unit of Measure"
                                    defaultValue={props.productGroup.unit_of_measure}
                                    onChange={(e) =>
                                        setProductGroupUnitOfMeasure(e.target.value)
                                    }
                                    invalid={
                                        editProductGroupMutation.isError &&
                                        editProductGroupMutation.error.response.data.unit_of_measure
                                    }
                                />
                                {editProductGroupMutation.isError && editProductGroupMutation.error.response.data.unit_of_measure && (
                                    <FormFeedback invalid>
                                        {editProductGroupMutation.error.response.data.unit_of_measure}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="tax_type">Tax Type</Label>
                                <Input
                                    type="select"
                                    id="inputState"
                                    className="form-control mb-2"
                                    defaultValue={props.productGroup.tax_type}
                                    onChange={(e) => setProductGroupTaxType(e.target.value)}
                                >
                                    <option value="">--</option>
                                    <option value={"TAX-INCLUSIVE"}>Tax Inclusive</option>
                                    <option value={"TAX-EXCLUSIVE"}>Tax Exclusive</option>

                                    invalid={
                                    editProductGroupMutation.isError &&
                                    editProductGroupMutation.error.response.data.tax_type
                                }
                                </Input>
                                {editProductGroupMutation.isError && editProductGroupMutation.error.response.data.tax_type && (
                                    <FormFeedback invalid>
                                        {editProductGroupMutation.error.response.data.tax_type}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="tax_percentage">Tax Percentage</Label>
                                <Input
                                    type="text"
                                    name="tax_percentage"
                                    id="tax_percentage"
                                    placeholder="Product Group Tax Percentage"
                                    defaultValue={props.productGroup.tax_percentage}
                                    onChange={(e) =>
                                        setProductGroupTaxPercentage(e.target.value)
                                    }
                                    invalid={
                                        editProductGroupMutation.isError &&
                                        editProductGroupMutation.error.response.data.tax_percentage
                                    }
                                />
                                {editProductGroupMutation.isError && editProductGroupMutation.error.response.data.tax_percentage && (
                                    <FormFeedback invalid>
                                        {editProductGroupMutation.error.response.data.tax_percentage}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        <FormGroup>
                            <Label for="product-group-image">Image</Label>
                            <Input
                                type="file"
                                name="product-group-image"
                                id="product-group-image"
                                // value={props.productGroup.image}
                                onChange={(e) =>
                                    setProductGroupImage(e.target.files[0])
                                }
                                invalid={
                                    editProductGroupMutation.isError &&
                                    editProductGroupMutation.error.response.data.image
                                }
                            />
                            {/*<FormText color="muted">*/}
                            {/*    {props.productGroup.image}*/}
                            {/*</FormText>*/}
                            {editProductGroupMutation.isError && editProductGroupMutation.error.response.data.image && (
                                <FormFeedback invalid>
                                    {editProductGroupMutation.error.response.data.image}
                                </FormFeedback>
                            )}
                        </FormGroup>

                        <Button
                            color="#4d5bed"
                            className="bx-btn-primary"
                            block
                            disabled={editProductGroupMutation.isLoading}
                        >
                            {editProductGroupMutation.isLoading? <><Loader size={'small'}/> Updating Product Group...</>: `Update Product Group`}

                        </Button>
                    </Form>
                </ModalBody>
            </Modal>
        </td>
    );
}

export default ActionButton;